import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const DIRECT_SOURCE_LABEL = 'Directo / Sin UTM';
export const DIRECT_FILTER_VALUE = '__direct__';

export interface UserUtmSummary {
  userId: string;
  source: string | null;
  isDirect: boolean;
}

export interface UserUtmDetail {
  user_id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_path: string | null;
  first_seen_at: string;
  is_direct: boolean;
}

/** Etiqueta de fuente. Única función de etiquetado de toda la feature. */
export function sourceLabel(row: { source: string | null; isDirect: boolean } | null | undefined): string {
  if (!row || row.isDirect || !row.source) return DIRECT_SOURCE_LABEL;
  return row.source;
}

/** Mapa userId → fuente, para la columna y el filtro de la tabla. */
export function useUsersUtmAttribution(enabled: boolean): {
  map: Map<string, UserUtmSummary>;
  sources: string[];
  isLoading: boolean;
  error: unknown;
} {
  const query = useQuery({
    queryKey: ['users-utm-attribution'],
    queryFn: async (): Promise<UserUtmSummary[]> => {
      const PAGE_SIZE = 1000;
      let all: UserUtmSummary[] = [];
      let page = 0;
      while (true) {
        const { data, error } = await (supabase as any)
          .from('user_utm_attribution')
          .select('user_id, utm_source, is_direct')
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all = all.concat(
          data.map((row: any) => ({
            userId: row.user_id as string,
            source: row.utm_source as string | null,
            isDirect: row.is_direct as boolean,
          })),
        );
        if (data.length < PAGE_SIZE) break;
        page++;
      }
      return all;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows = query.data ?? [];
  const map = new Map<string, UserUtmSummary>(rows.map((r) => [r.userId, r]));
  const sources = Array.from(
    new Set(rows.filter((r) => !r.isDirect && r.source).map((r) => r.source as string)),
  ).sort((a, b) => a.localeCompare(b));

  return { map, sources, isLoading: query.isLoading, error: query.error };
}

/** Detalle de un usuario, on-demand al abrir el diálogo. */
export function useUserUtmDetail(userId: string | null): UseQueryResult<UserUtmDetail | null> {
  return useQuery({
    queryKey: ['user-utm-attribution', userId],
    queryFn: async (): Promise<UserUtmDetail | null> => {
      const { data, error } = await (supabase as any)
        .from('user_utm_attribution')
        .select('user_id, utm_source, utm_medium, utm_campaign, utm_content, utm_term, landing_path, first_seen_at, is_direct')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return (data as UserUtmDetail) ?? null;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
